import { paths } from './paths';

/**
 * Where somebody was going when they were sent to sign in, and how it is made
 * safe to act on.
 *
 * A signed-out user who types `/users` should land back on `/users` after
 * signing in rather than on the dashboard, so the destination has to survive the
 * trip. That destination is the classic open-redirect: a value that decides
 * where the browser goes next, carried across a screen, and — the moment it can
 * be written from outside — a link that reads as yours and ends up on somebody
 * else's origin with a session freshly minted.
 *
 * Two things close it here.
 *
 * **It is carried in the router's location state, not in the URL.** A query
 * parameter is a thing anyone can put in a link and send to a member of staff;
 * history state is written by this app, on a navigation this app performed.
 * That is not a guarantee — it is a much smaller surface, and it is why the
 * check below still runs.
 *
 * **It must be a path on this origin, and it is only ever used as one.** The
 * check is structural and does not consult `window.location`: anything that is
 * not a single leading slash followed by a path is discarded rather than
 * repaired, because "make this dangerous string safe" is a game the string wins
 * eventually.
 */

/** The shape the guard writes into location state when it bounces to sign-in. */
export interface ReturnState {
  readonly from: string;
}

/**
 * The destination in `state`, if it is one this app may navigate to.
 *
 * `unknown` in, because `useLocation().state` is whatever the previous
 * navigation put there — including, after a reload, whatever was in
 * `history.state` from a session this code did not write.
 */
export function returnPathFrom(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;

  return safeReturnPath((state as { from?: unknown }).from);
}

/**
 * `candidate` if it is a root-relative path on this origin, otherwise `null`.
 *
 * Rejected, in order:
 *
 * - anything that is not a non-empty string — `state` survives a reload and is
 *   not typed on the way back in;
 * - anything not starting with `/` — that covers every absolute URL
 *   (`https://elsewhere/`), every scheme (`javascript:`), and every bare word
 *   the router would resolve relative to the current route;
 * - `//host` and `/\host` — protocol-relative URLs, which start with a slash
 *   and go to another origin anyway. The backslash form is not a curiosity:
 *   browsers normalise `\` to `/` in this position, so rejecting only `//`
 *   leaves the hole open;
 * - the sign-in path itself, which would send somebody who has just signed in
 *   back to the screen they signed in on.
 */
export function safeReturnPath(candidate: unknown): string | null {
  if (typeof candidate !== 'string' || candidate === '') return null;

  if (candidate[0] !== '/') return null;
  if (candidate[1] === '/' || candidate[1] === '\\') return null;

  // The path, without the query or fragment. Both are kept in the value that is
  // navigated to — a filtered list is worth returning to — but neither says
  // anything about which route this is.
  const [pathname = ''] = candidate.split(/[?#]/);

  if (pathname === paths.signIn) return null;

  return candidate;
}
